import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Link,
  Typography,
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

/**
 * Material UI Footer component
 * @returns {React.ReactNode}
 */
const Footer = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();
  
  return (
    <Box 
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        py: 4,
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="space-between">
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
              component={RouterLink}
              to="/"
            >
              Membership Suite
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Modern membership management solution for organizations of all sizes.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                size="small" 
                aria-label="Facebook" 
                component="a" 
                href="#"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: '#3b5998' }
                }}
              >
                <FacebookIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                aria-label="Twitter" 
                component="a" 
                href="#"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: '#1DA1F2' }
                }}
              >
                <TwitterIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                aria-label="LinkedIn" 
                component="a" 
                href="#"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: '#0077b5' }
                }}
              >
                <LinkedInIcon fontSize="small" />
              </IconButton>
            </Box>
          </Grid>
          
          <Grid item xs={6} sm={3} md={2}>
            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2, fontWeight: 600 }}>
              Product
            </Typography>
            <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
              {['Features', 'Pricing', 'Testimonials', 'FAQ'].map((item) => (
                <Box component="li" key={item} sx={{ mb: 1 }}>
                  <Link
                    component={RouterLink}
                    to={`/${item.toLowerCase()}`}
                    underline="hover"
                    color="text.secondary"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {item}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={6} sm={3} md={2}>
            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2, fontWeight: 600 }}>
              Company
            </Typography>
            <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
              {['About', 'Blog', 'Careers', 'Contact'].map((item) => (
                <Box component="li" key={item} sx={{ mb: 1 }}>
                  <Link
                    component={RouterLink}
                    to={`/${item.toLowerCase()}`}
                    underline="hover"
                    color="text.secondary"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {item}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>
          
          <Grid item xs={6} sm={3} md={2}>
            <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2, fontWeight: 600 }}>
              Legal
            </Typography>
            <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
              {['Privacy', 'Terms', 'Security', 'Accessibility'].map((item) => (
                <Box component="li" key={item} sx={{ mb: 1 }}>
                  <Link
                    component={RouterLink}
                    to={`/${item.toLowerCase()}`}
                    underline="hover"
                    color="text.secondary"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {item}
                  </Link>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ mt: 4, mb: 2 }} />
        
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            &copy; {currentYear} Membership Suite. All rights reserved.
          </Typography>
          <Box sx={{ mt: { xs: 2, sm: 0 } }}>
            <Typography variant="caption" color="text.secondary">
              Made with ❤️ by Scrumban Ai
            </Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
